import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { format as rutFormat } from "rut.js";
import { RequestType, requestTypeExtendedTranslation } from "../../../interfaces";
import { alpha, Avatar, Box, Skeleton, styled } from "@mui/material";
import {
  PrescriptionIcon,
  CompoundedPrescriptionIcon,
  MedicalOrderIcon,
  MedicalCertificateIcon,
} from "@icons";
import { ObscuredText } from "@components";

interface CardContainerProps {
  documentCard: boolean;
  documentType?: RequestType;
}

interface CardTitleProps {
  showEllipsis?: boolean;
}

const CardContainer = styled(Box, {
  shouldForwardProp: (prop: string) => prop !== "documentCard" && prop !== "documentType",
})<CardContainerProps>(({ theme, documentCard, documentType }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0px 0px 20px 1px #0000000f",
  borderLeft:
    documentCard && documentType
      ? `12px solid ${theme.palette.documentTypes[documentType]}`
      : "none",
  height: 80,
  padding: documentCard ? "0px 16px" : "6px 6px 6px 12px",
  cursor: "pointer",
  width: "100%",
  borderRadius: "4px",
}));

const Column = styled(Box)(() => ({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  marginRight: "4px",
}));

const Row = styled(Box)(() => ({
  width: "100%",
  height: "100%",
  minHeight: "20px",
  display: "flex",
  alignItems: "center",
}));

const CardTitle = styled(Box)<CardTitleProps>(({ theme }) => ({
  maxWidth: "300px",
  maxHeight: "24px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const CardRut = styled(Box)(({ theme }) => ({
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "23px",
  letterSpacing: "0.5px",
  color: theme.palette.text.secondary,
}));

const ArrowColumn = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledRightArrow = styled(KeyboardArrowRightIcon)(({ theme }) => ({
  color: theme.palette.icon.primary,
}));

const StyledAvatar = styled(Avatar, {
  shouldForwardProp: (prop: string) => prop !== "documentType",
})<{ documentType?: RequestType }>(({ theme, documentType }) => ({
  marginRight: "16px",
  color: documentType
    ? theme.palette.documentTypes[documentType]
    : theme.palette.background.paper,
  backgroundColor: documentType
    ? alpha(theme.palette.documentTypes[documentType], 0.2)
    : theme.palette.background.paper,
}));

const requestTypeIconMapper: Record<RequestType, React.ReactNode> = {
  [RequestType.PRESCRIPTION]: <PrescriptionIcon />,
  [RequestType.COMPOUNDED_PRESCRIPTION]: <CompoundedPrescriptionIcon />,
  [RequestType.MEDICAL_ORDER]: <MedicalOrderIcon />,
  [RequestType.MEDICAL_CERTIFICATE]: <MedicalCertificateIcon />,
};

interface CardProps {
  documentType?: RequestType;
  onClick?: () => void;
}

export function Card({ documentType, onClick }: CardProps) {
  const cardTitle = documentType ? requestTypeExtendedTranslation[documentType] : "";

  const requestIcon = documentType ? requestTypeIconMapper[documentType] : null;

  return (
    <CardContainer
      documentCard={Boolean(documentType)}
      documentType={documentType}
      onClick={onClick}
    >
      {requestIcon && (
        <StyledAvatar documentType={documentType}>{requestIcon}</StyledAvatar>
      )}
      <Column>
        <Row>
          <CardTitle>{cardTitle}</CardTitle>
        </Row>
      </Column>
      <ArrowColumn>
        <StyledRightArrow />
      </ArrowColumn>
    </CardContainer>
  );
}

Card.Skeleton = function CardSkeleton() {
  return (
    <CardContainer documentCard={false}>
      <Column>
        <Row>
          <CardTitle>
            <Skeleton variant="text" width={200} />
          </CardTitle>
        </Row>
        <Row>
          <CardRut>
            <Skeleton variant="text" width={115} />
          </CardRut>
        </Row>
      </Column>
      <ArrowColumn>
        <StyledRightArrow />
      </ArrowColumn>
    </CardContainer>
  );
};

interface PatientCardProps {
  name: string;
  lastName: string;
  rut?: string;
  passport?: string;
  onClick?: () => void;
}

const StyledPatientBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "16px",
  borderBottom: `1px solid ${theme.palette.divider}`,
  ":last-child": {
    borderBottom: "none",
  },
  cursor: "pointer",
}));

export const PatientCard: React.FC<PatientCardProps> = ({
  name,
  lastName,
  rut,
  passport,

  onClick,
}) => {
  return (
    <StyledPatientBox onClick={onClick}>
      <Column>
        <Row>
          <ObscuredText component={CardTitle}>{`${name} ${lastName}`}</ObscuredText>
        </Row>
        <Row>
          <ObscuredText component={CardRut}>
            {rut ? rutFormat(rut) : passport ? passport : null}
          </ObscuredText>
        </Row>
      </Column>
      <ArrowColumn>
        <StyledRightArrow />
      </ArrowColumn>
    </StyledPatientBox>
  );
};
